import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone,
  Check,
  ArrowRight,
  Shield,
  Calendar,
  CheckCircle2,
  Sparkles,
  Clock,
  Building2,
  Headphones,
  Star,
  FileText,
  BarChart3,
  Globe,
  MessageSquare,
  TrendingUp,
  DollarSign,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/use-page-meta";
import { JsonLd, pricingSchema, faqSchema } from "@/components/seo/JsonLd";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";

/**
 * PRICING PAGE — Custom AI-Powered Business Operations Platforms (March 2026)
 *
 * PRICING:
 * - Starting from $999/mo (annual) / $1,200/mo (monthly)
 * - Setup fees vary by project scope
 *
 * Positioned as custom solutions for businesses & home services.
 * Mortgage broker product has its own dedicated page at /solutions/mortgage-brokers.
 */

type BillingPeriod = "annual" | "monthly";

const platformFeatures = [
  {
    category: "AI Receptionist & Phone System",
    icon: Phone,
    features: [
      "24/7 AI Phone Receptionist (<2s response)",
      "Intelligent inquiry handling & customer education",
      "Real-time appointment booking during calls",
      "Smart call routing & business hours awareness",
      "Call transcripts, summaries & sentiment analysis",
      "Custom brand voice & personalized greeting",
      "Emergency escalation protocols",
    ],
  },
  {
    category: "Call Analytics & Lead Pipeline",
    icon: BarChart3,
    features: [
      "Call volume trends & peak hour analysis",
      "Booking conversion metrics",
      "Automated lead creation from AI calls",
      "Visual pipeline from inquiry to close",
      "Priority scoring (Hot / Warm / Cold)",
      "Activity timeline & follow-up reminders",
      "Kanban + list views",
    ],
  },
  {
    category: "Custom AI Integrations",
    icon: Wrench,
    features: [
      "Industry-specific AI training & knowledge",
      "Custom tool integrations for your workflow",
      "Automated data extraction & processing",
      "Third-party API connections",
      "Custom reporting dashboards",
      "Workflow automation tailored to your business",
    ],
  },
  {
    category: "Professional Website",
    icon: Globe,
    features: [
      "Custom-designed multi-page website",
      "Industry-specific landing pages",
      "Service & pricing display widgets",
      "Blog with AI-generated content",
      "Review showcase & testimonials",
      "Mobile-responsive PWA",
      "SEO-optimized with schema markup",
    ],
  },
  {
    category: "Review Intelligence",
    icon: Star,
    features: [
      "Review monitoring dashboard",
      "AI-drafted review responses",
      "Sentiment analysis across platforms",
      "Automated review solicitation",
      "Testimonial graphics generator",
      "Google Business Profile management",
    ],
  },
  {
    category: "Content & Marketing",
    icon: MessageSquare,
    features: [
      "AI Blog Manager (1,500-word SEO posts in 2 min)",
      "Social Studio (branded social images)",
      "SMS marketing automation",
      "Web chat widget for website visitors",
      "Content calendar & scheduling",
    ],
  },
  {
    category: "Documents & Calendar",
    icon: FileText,
    features: [
      "Secure document collection via magic link",
      "No client account creation needed",
      "Encrypted storage & compliance-ready",
      "Google Calendar integration (OAuth)",
      "Real-time availability during AI calls",
      "Confirmation SMS & reminders",
    ],
  },
  {
    category: "Admin & Support",
    icon: Shield,
    features: [
      "Dedicated onboarding support",
      "Priority phone & email support",
      "Monthly strategy calls",
      "Custom branding & business info",
      "Team member management",
      "Notification preferences",
    ],
  },
];

export default function Pricing() {
  usePageMeta({
    title: "Pricing",
    description:
      "Custom AI-Powered Business Operations Platforms by Myna AI. Starting from $999/mo. AI receptionist, professional website, lead pipeline, reviews, and more.",
    path: "/pricing",
  });
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("annual");

  const price = billingPeriod === "annual" ? 999 : 1200;
  const monthlySavings = 1200 - 999; // $201
  const annualSavings = monthlySavings * 12; // $2,412

  return (
    <div className="min-h-screen bg-[var(--m-bg)] text-[var(--m-text-primary)]">
      <JsonLd data={pricingSchema} />
      <JsonLd data={faqSchema} />
      {/* Gradient wash background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--m-bg-gradient-from)] via-[var(--m-bg-gradient-via)] to-[var(--m-bg-gradient-to)]" />
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px]"
          style={{
            background:
              "radial-gradient(circle, rgba(132,204,22,0.025) 0%, transparent 70%)",
            filter: "blur(140px)",
          }}
        />
      </div>

      <Navbar />

      <div className="relative z-10 pt-24">
        {/* Hero Section */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="px-3 py-1.5 bg-[var(--m-badge-bg)] border border-[var(--m-ring-border)]">
                <span className="text-xs text-[var(--m-lime-text)] font-semibold tracking-wide">
                  CUSTOM SOLUTIONS &middot; TAILORED PRICING &middot; EVERYTHING
                  INCLUDED
                </span>
              </div>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.06] mb-8 text-[var(--m-heading)]">
              Custom AI Platforms
              <br />
              <span className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent">Built for Your Business</span>
            </h1>

            <p className="text-xl text-[var(--m-body)] leading-relaxed mb-10 max-w-3xl mx-auto">
              We build custom AI-powered business operations platforms tailored
              to your industry. AI receptionist, professional website, lead
              pipeline, reviews, content, and more &mdash; designed specifically
              for how you work.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-1 p-1.5 bg-[var(--m-card)] border border-[var(--m-border-strong)] mb-6">
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`relative px-8 py-3 text-sm font-semibold transition-all duration-300 ${
                  billingPeriod === "annual"
                    ? "text-[#292929]"
                    : "text-[var(--m-muted)] hover:text-[var(--m-heading)]"
                }`}
              >
                {billingPeriod === "annual" && (
                  <motion.div
                    layoutId="billing-bg"
                    className="absolute inset-0 bg-lime-500/80"
                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  Annual
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-lime-900/60 text-lime-300 text-xs font-semibold border border-[var(--m-ring-border)]">
                    <Sparkles className="w-3 h-3" />
                    RECOMMENDED
                  </span>
                </span>
              </button>
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`relative px-8 py-3 text-sm font-semibold transition-all duration-300 ${
                  billingPeriod === "monthly"
                    ? "text-[#292929]"
                    : "text-[var(--m-muted)] hover:text-[var(--m-heading)]"
                }`}
              >
                {billingPeriod === "monthly" && (
                  <motion.div
                    layoutId="billing-bg"
                    className="absolute inset-0 bg-lime-500/80"
                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                  />
                )}
                <span className="relative z-10">Month-to-Month</span>
              </button>
            </div>

            {/* Savings Callout */}
            <AnimatePresence mode="wait">
              {billingPeriod === "annual" && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-center gap-2 text-sm text-[var(--m-lime-text)]"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    Save ${monthlySavings}/mo with annual commitment ($
                    {annualSavings.toLocaleString()}/yr total savings)
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </section>

        {/* Single Pricing Card */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 pb-20">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative p-10 bg-gradient-to-br from-[var(--m-bg-gradient-from)] via-[var(--m-card-alt)] to-[var(--m-card)] border-2 border-lime-500/50 shadow-2xl shadow-lime-500/20"
              style={{
                boxShadow:
                  "0 0 60px rgba(132, 204, 22, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-lime-500/[0.03] via-transparent to-teal-500/[0.02] pointer-events-none" />

              {/* Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-6 py-1.5 shadow-lg bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] shadow-lime-500/30">
                <span className="text-xs font-black tracking-wider">
                  EVERYTHING INCLUDED
                </span>
              </div>

              <div className="relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-[var(--m-heading)] mb-2">
                    Business Operations Platform
                  </h3>
                  <p className="text-[var(--m-muted)]">
                    Custom-built for your industry
                  </p>
                </div>

                {/* Price Display */}
                <div className="text-center mb-8 pb-8 border-b border-[var(--m-border)]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={billingPeriod}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-baseline justify-center gap-2 mb-1">
                        <span className="text-lg text-[var(--m-muted)] font-medium">
                          Starting from
                        </span>
                      </div>
                      <div className="flex items-baseline justify-center gap-2 mb-3">
                        <span className="text-6xl font-bold text-[var(--m-heading)]">
                          ${price.toLocaleString()}
                        </span>
                        <span className="text-[var(--m-subtle)] text-xl">/month</span>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm text-[var(--m-muted)]">
                          + one-time setup fee starting from <span className="text-[var(--m-heading)] font-semibold">$2,999</span>
                        </div>
                        {billingPeriod === "annual" && (
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-lime-400/70" />
                            <span className="text-sm text-lime-400/70">
                              Save ${annualSavings.toLocaleString()}/yr vs.
                              month-to-month
                            </span>
                          </div>
                        )}
                        {billingPeriod === "monthly" && (
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-sm text-[var(--m-subtle)]">
                              Switch to annual to save $
                              {monthlySavings}/mo
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Quick Feature Highlights */}
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    "24/7 AI Phone Receptionist",
                    "Custom Multi-Page Website",
                    "Industry-Specific AI Training",
                    "Lead Pipeline & CRM",
                    "Review Intelligence",
                    "AI Blog & Content",
                    "Secure Document Portal",
                    "Google Calendar Booking",
                    "Call Analytics Dashboard",
                    "Social Studio",
                    "Custom Integrations",
                    "Priority Support",
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check
                        className="w-4 h-4 text-lime-400 shrink-0"
                        strokeWidth={2}
                      />
                      <span className="text-sm text-[var(--m-body)]">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Link to="/request-demo">
                  <Button className="w-full px-6 py-5 bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] font-bold text-lg hover:from-lime-600 hover:to-lime-500 shadow-lg shadow-lime-500/30 transition-all focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--m-bg)]">
                    Book a Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <p className="text-center text-xs text-[var(--m-subtle)] mt-4">
                  Custom scope &amp; pricing after discovery call &middot; No
                  obligation
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Terms & Commitment Info */}
        <div className="max-w-[1600px] mx-auto px-8 md:px-12">
          <div className="max-w-2xl mx-auto mt-0 mb-4">
            <div className="p-6 bg-[var(--m-card)] border border-[var(--m-border-strong)]">
              <div className="flex items-start gap-3 mb-4">
                <FileText className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-[var(--m-heading)] mb-2">Commitment &amp; Terms</h4>
                  <div className="text-sm text-[var(--m-muted)] space-y-2">
                    {billingPeriod === "annual" ? (
                      <>
                        <p>Annual plans require a <span className="text-amber-400 font-medium">12-month commitment</span> at the discounted rate. Billed monthly.</p>
                        <p>Early termination includes a $1,500 cancellation fee + 50% of remaining monthly fees. Setup fees are non-refundable.</p>
                      </>
                    ) : (
                      <p>Month-to-month plans can be cancelled anytime with 30 days notice. Setup fees are non-refundable.</p>
                    )}
                    <p className="text-xs text-[var(--m-subtle)] mt-3">
                      By booking a consultation, you acknowledge our{" "}
                      <Link to="/terms" className="text-lime-400/70 hover:text-[var(--m-lime)] underline">Terms of Service</Link>
                      {" "}and{" "}
                      <Link to="/privacy" className="text-lime-400/70 hover:text-[var(--m-lime)] underline">Privacy Policy</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badges Row */}
          <div className="flex flex-wrap justify-center gap-8 text-[var(--m-subtle)] mt-8 mb-16">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-lime-400/50" />
              <span className="text-sm">Secure Payment via Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-lime-400/50" />
              <span className="text-sm">Live within 5-7 Business Days</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-lime-400/50" />
              <span className="text-sm">99.9% Uptime SLA</span>
            </div>
          </div>
        </div>

        {/* Industries We Serve */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
              Built for Your Industry
            </h2>
            <p className="text-xl text-[var(--m-muted)] max-w-3xl mx-auto">
              Every platform is custom-designed for your specific business.
              We&apos;ve built solutions across multiple industries &mdash; and
              we&apos;re always taking on new projects.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Mortgage Brokers",
                description:
                  "AI receptionist with compliance guardrails, rate manager, document vault, and 50+ page websites with city landing pages.",
                link: "/solutions/mortgage-brokers",
                hasPage: true,
              },
              {
                name: "Home Services & HVAC",
                description:
                  "24/7 call answering, emergency dispatch, job scheduling, technician routing, and customer management.",
                link: null,
                hasPage: false,
              },
              {
                name: "Custom Projects",
                description:
                  "Have a unique business? We'll scope a custom AI platform tailored to your operations, workflow, and industry requirements.",
                link: "/request-demo",
                hasPage: true,
              },
            ].map((industry) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-8 bg-[var(--m-card)] border border-[var(--m-border-strong)] flex flex-col"
              >
                <h3 className="text-xl font-bold text-[var(--m-heading)] mb-3">
                  {industry.name}
                </h3>
                <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-6 flex-1">
                  {industry.description}
                </p>
                {industry.hasPage && industry.link && (
                  <Link
                    to={industry.link}
                    className="text-sm text-[var(--m-lime-text)] font-semibold hover:text-[var(--m-lime)] transition-colors inline-flex items-center gap-1"
                  >
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {!industry.hasPage && (
                  <span className="text-sm text-[var(--m-subtle)] font-medium">
                    Coming Soon
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* TCO Comparison */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
              What You&apos;re Currently Paying
            </h2>
            <p className="text-xl text-[var(--m-muted)] max-w-3xl mx-auto">
              Most businesses juggle 6+ separate tools and services. Here&apos;s
              what that actually costs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Current Tools Cost */}
            <div className="p-8 bg-[var(--m-card)] border border-[var(--m-border-strong)]">
              <h3 className="text-2xl font-bold text-[var(--m-heading)] mb-6">
                Without Myna AI
              </h3>
              <div className="space-y-4">
                {[
                  {
                    name: "Receptionist / Answering Service",
                    cost: "$1,500 - 3,000",
                    icon: Headphones,
                  },
                  {
                    name: "CRM / Lead Management",
                    cost: "$50 - 200",
                    icon: TrendingUp,
                  },
                  {
                    name: "Professional Website",
                    cost: "$100 - 500",
                    icon: Globe,
                  },
                  {
                    name: "Review Management",
                    cost: "$300 - 500",
                    icon: Star,
                  },
                  {
                    name: "SEO & Content Marketing",
                    cost: "$500 - 2,000",
                    icon: BarChart3,
                  },
                  {
                    name: "Social Media Management",
                    cost: "$300 - 500",
                    icon: MessageSquare,
                  },
                  {
                    name: "Document Management",
                    cost: "$25 - 100",
                    icon: FileText,
                  },
                ].map((tool) => (
                  <div
                    key={tool.name}
                    className="flex items-center justify-between pb-3 border-b border-[var(--m-border)]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-[var(--m-accent-bg-red)] border border-red-500/20">
                        <tool.icon className="w-4 h-4 text-red-400/70" />
                      </div>
                      <span className="text-sm text-[var(--m-body)]">{tool.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[var(--m-muted)]">
                      {tool.cost}/mo
                    </span>
                  </div>
                ))}

                <div className="pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-[var(--m-heading)]">
                      Total Monthly Cost
                    </span>
                    <span className="text-2xl font-bold text-red-400">
                      $2,775 - 6,800
                    </span>
                  </div>
                  <div className="text-xs text-[var(--m-subtle)] mt-1 text-right">
                    Plus hours managing each tool separately
                  </div>
                </div>
              </div>
            </div>

            {/* Myna Cost */}
            <div className="p-8 bg-[var(--m-card)] border-2 border-[var(--m-ring-border)]">
              <h3 className="text-2xl font-bold text-[var(--m-heading)] mb-6">
                With Myna AI
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-[var(--m-border)]">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-[var(--m-badge-bg)] border border-[var(--m-badge-border)]">
                      <Phone className="w-4 h-4 text-lime-400" />
                    </div>
                    <div>
                      <div className="text-sm text-[var(--m-heading)] font-semibold">
                        Custom Business Operations Platform
                      </div>
                      <div className="text-xs text-lime-400/70">
                        All tools included, one login, one price
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[var(--m-subtle)]">Starting from</div>
                    <div className="text-2xl font-bold text-lime-400">
                      $999/mo
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-[var(--m-accent-bg-lime)] border border-[var(--m-badge-border)] mt-4">
                  <div className="text-center">
                    <div className="text-sm text-[var(--m-muted)] mb-2">
                      Potential Savings vs. Separate Tools
                    </div>
                    <div className="text-4xl font-bold text-[var(--m-lime-text)] mb-1">
                      $1,776 - 5,801/mo
                    </div>
                    <div className="text-xs text-[var(--m-subtle)]">
                      Plus: 15-20 hours/week reclaimed from manual tasks
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)] text-center">
                    <div className="text-2xl font-bold text-[var(--m-heading)]">10+</div>
                    <div className="text-xs text-[var(--m-subtle)] mt-1">
                      Tools Replaced
                    </div>
                  </div>
                  <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)] text-center">
                    <div className="text-2xl font-bold text-[var(--m-heading)]">1</div>
                    <div className="text-xs text-[var(--m-subtle)] mt-1">Login</div>
                  </div>
                  <div className="p-4 bg-[var(--m-elevated)] border border-[var(--m-border)] text-center">
                    <div className="text-2xl font-bold text-[var(--m-heading)]">24/7</div>
                    <div className="text-xs text-[var(--m-subtle)] mt-1">
                      AI Coverage
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Section */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
              The ROI Is Unmistakable
            </h2>
            <p className="text-xl text-[var(--m-muted)] max-w-3xl mx-auto">
              Just one or two additional customers per month pays for the entire
              platform multiple times over.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="p-6 bg-[var(--m-card)] border-l-4 border-red-500/40">
              <div className="flex items-start gap-4">
                <Phone
                  className="w-6 h-6 text-red-400/70 shrink-0 mt-1"
                  strokeWidth={1.5}
                />
                <div>
                  <h4 className="text-lg font-bold text-[var(--m-heading)] mb-2">
                    The Missed Call Problem
                  </h4>
                  <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-4">
                    62% of callers won&apos;t leave a voicemail. 80% won&apos;t
                    call back. Every missed call is revenue walking to your
                    competitor.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[var(--m-card)] border-l-4 border-lime-500/40">
              <div className="flex items-start gap-4">
                <Calendar
                  className="w-6 h-6 text-lime-400/70 shrink-0 mt-1"
                  strokeWidth={1.5}
                />
                <div>
                  <h4 className="text-lg font-bold text-[var(--m-heading)] mb-2">
                    Captured Opportunities
                  </h4>
                  <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-4">
                    Your AI captures every inquiry, books appointments
                    instantly, and never puts a customer on hold &mdash; even at
                    2am on a Saturday.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[var(--m-card)] border-l-4 border-teal-500/40">
              <div className="flex items-start gap-4">
                <DollarSign
                  className="w-6 h-6 text-teal-400/70 shrink-0 mt-1"
                  strokeWidth={1.5}
                />
                <div>
                  <h4 className="text-lg font-bold text-[var(--m-heading)] mb-2">
                    300-500% ROI
                  </h4>
                  <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-4">
                    One or two extra customers per month from previously missed
                    calls more than covers the entire platform cost. Everything
                    else is profit.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 p-8 bg-[var(--m-accent-bg-lime)] border-2 border-[var(--m-ring-border)]">
            <div className="text-center">
              <div className="text-sm text-[var(--m-muted)] mb-2">
                The platform pays for itself with just
              </div>
              <div className="text-5xl font-bold text-[var(--m-heading)] mb-2">
                1-2 extra customers/month
              </div>
              <div className="text-lg text-[var(--m-lime-text)]">
                Everything beyond that is pure profit
              </div>
            </div>
          </div>
        </section>

        {/* Full Feature Breakdown */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
              Everything That&apos;s Included
            </h2>
            <p className="text-xl text-[var(--m-muted)] max-w-3xl mx-auto">
              No feature gating. No per-seat charges. No hidden costs. Every
              feature, every tool, every update &mdash; included.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platformFeatures.map((category) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-6 bg-[var(--m-card)] border border-[var(--m-border-strong)]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-[var(--m-badge-bg)] border border-[var(--m-badge-border)]">
                    <category.icon className="w-5 h-5 text-lime-400" />
                  </div>
                  <h3 className="text-sm font-bold text-[var(--m-heading)]">
                    {category.category}
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {category.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check
                        className="w-3.5 h-3.5 text-lime-400/70 shrink-0 mt-0.5"
                        strokeWidth={2}
                      />
                      <span className="text-xs text-[var(--m-muted)] leading-relaxed">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* After-Hours Value Prop */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
                Your AI Works
                <br />
                <span className="text-[var(--m-lime-text)]">
                  While You Sleep
                </span>
              </h2>
              <p className="text-xl text-[var(--m-muted)] leading-relaxed mb-8">
                A $3,500/month receptionist works 40 hours a week. Your AI
                receptionist works 168 hours a week starting from ${price}
                /month. No sick days, no vacations, no training time.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-lime-400/70" />
                  <span className="text-[var(--m-body)]">
                    40% of calls happen outside business hours
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-lime-400/70" />
                  <span className="text-[var(--m-body)]">
                    Customers expect instant responses, day or night
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-lime-400/70" />
                  <span className="text-[var(--m-body)]">
                    AI answers every call in under 2 seconds
                  </span>
                </div>
              </div>
            </div>

            <div className="p-8 bg-[var(--m-card)] border border-[var(--m-border-strong)]">
              <h3 className="text-2xl font-bold text-[var(--m-heading)] mb-6">
                Receptionist Comparison
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-[var(--m-border)]">
                  <div>
                    <div className="text-sm text-[var(--m-body)]">
                      Full-time receptionist
                    </div>
                    <div className="text-xs text-[var(--m-subtle)]">
                      40 hrs/wk, salary + benefits
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[var(--m-muted)]">
                    $3,500+/mo
                  </div>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-[var(--m-border)]">
                  <div>
                    <div className="text-sm text-[var(--m-body)]">
                      Answering service
                    </div>
                    <div className="text-xs text-[var(--m-subtle)]">
                      Generic, no booking
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[var(--m-muted)]">
                    $800-1,500/mo
                  </div>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-[var(--m-border)]">
                  <div>
                    <div className="text-sm text-[var(--m-heading)] font-semibold">
                      Myna AI Platform
                    </div>
                    <div className="text-xs text-lime-400/70">
                      168 hrs/wk, books appointments, answers inquiries
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[var(--m-subtle)]">Starting from</div>
                    <div className="text-xl font-bold text-lime-400">
                      ${price}/mo
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                q: "What does 'starting from' mean?",
                a: "Every platform is custom-scoped based on your business needs. $999/mo is the starting price for our core platform. After a discovery call, we'll provide exact pricing based on your requirements, integrations, and customization needs.",
              },
              {
                q: "What's the difference between Annual and Month-to-Month?",
                a: `Annual plans save $${monthlySavings}/mo ($${annualSavings.toLocaleString()}/yr). Both include setup fees. Annual locks in your rate for 12 months. Month-to-Month offers flexibility with 30-day cancellation notice.`,
              },
              {
                q: "What's included in the setup fee?",
                a: "Custom AI receptionist training for your business, professional website build, Google Calendar integration, review intelligence setup, content configuration, and dedicated onboarding support. Setup fees vary by project scope.",
              },
              {
                q: "How quickly can I be up and running?",
                a: "Most platforms go live within 5-7 business days from signup. We need minimal input: business info, logo, calendar email, business hours, and service details. We handle everything else.",
              },
              {
                q: "Can the AI handle industry-specific questions?",
                a: "Absolutely. Your AI is custom-trained on your specific services, pricing, compliance requirements, and business processes. It handles inquiries, educates customers, and books appointments — all while following your guidelines.",
              },
              {
                q: "Is my data secure?",
                a: "Yes. Encrypted storage with multi-tenant isolation, secure document collection via magic links (no client accounts needed), and full audit trails. Your data is yours.",
              },
              {
                q: "What happens to my calls if there's an outage?",
                a: "99.9% uptime with automatic failover. In the rare case of an issue, calls automatically forward to your backup number. We've never had a complete outage.",
              },
              {
                q: "Can I cancel my subscription?",
                a: "Month-to-Month plans can be canceled anytime with 30 days notice. Annual plans have a 12-month commitment. We're confident you'll see ROI within the first month.",
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="p-6 bg-[var(--m-card)] border border-[var(--m-border-strong)]"
              >
                <h3 className="text-lg font-semibold text-[var(--m-heading)] mb-3">
                  {faq.q}
                </h3>
                <p className="text-sm text-[var(--m-muted)] leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-20 border-t border-[var(--m-border-strong)]">
          <div className="p-12 bg-[var(--m-accent-bg-lime)] border-2 border-[var(--m-ring-border)]">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--m-heading)]">
                Ready to Build Your Custom Platform?
              </h2>
              <p className="text-xl text-[var(--m-muted)] leading-relaxed mb-8 max-w-3xl mx-auto">
                Every business is different. Let&apos;s talk about what you
                need, and we&apos;ll design a platform that fits your operations
                perfectly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/request-demo">
                  <Button className="px-10 py-5 bg-lime-500/80 text-[#292929] font-bold text-lg hover:bg-lime-500 inline-flex items-center gap-3">
                    Book a Demo
                    <Calendar className="w-6 h-6" />
                  </Button>
                </Link>
                <Link to="/request-demo">
                  <Button className="px-10 py-5 bg-[var(--m-elevated)] border-2 border-[var(--m-border-strong)] text-[var(--m-heading)] font-semibold hover:bg-[var(--m-card)]">
                    Book a Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-[var(--m-subtle)] mt-6">
                Starting from ${price}/mo &middot; Custom setup &middot;
                Everything included &middot; No obligation
              </p>
            </div>
          </div>
        </section>

        {/* Continuous Improvement */}
        <section className="max-w-[1600px] mx-auto px-8 md:px-12 py-12">
          <div className="text-center p-8 border border-[var(--m-border)] bg-[var(--m-card)]">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-lime-400/70" />
              <span className="text-sm font-semibold text-[var(--m-body)]">
                CONTINUOUS IMPROVEMENT
              </span>
            </div>
            <p className="text-[var(--m-muted)] max-w-2xl mx-auto">
              We ship new features monthly. Your subscription includes all
              updates automatically at no extra cost. We&apos;re a partner, not
              a self-serve tool.
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
